import {useEffect} from "react";
import {useMutation} from "@apollo/client";
import {useIntegrations} from "../IntegrationContext";
import {useNavigate} from "react-router-dom";
import {UPDATE_SLACK_INTEGRATION} from "../../../../graphql/integrationQueries";
import {SET_VANTA_ACCESS_TOKEN} from "../../../../graphql/integrations/vantaQueries";
import {SET_ZOOM_ACCESS_TOKEN} from "../../../../graphql/integrations/zoomQueries";
import {SET_SALESFORCE_ACCESS_TOKEN} from "../../../../graphql/integrations/salesforceQueries";
import {consumeSalesforceCodeVerifier} from "../../../../resources/integrations/salesforce";
import {handleError} from "../../../../resources/utils/error";

const SALESFORCE_AUTHORIZATION_ERROR_MESSAGE =
  "Salesforce authorization failed. Please reconnect to Salesforce.";

export const useOauthHook = () => {
  const {updateIntegrationFields, selectedFormData, setSelectedFormData} =
    useIntegrations();
  const navigate = useNavigate();

  const [updateSlackIntegration] = useMutation(UPDATE_SLACK_INTEGRATION, {
    onCompleted: (data) => {
      updateIntegrationFields(data);
      setSelectedFormData(data.updateSlackIntegration.configuration);
    },
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const identifier = queryParams.get("identifier");

    if (identifier) {
      navigate("/integrations/slack", {replace: true, search: ""});
      updateSlackIntegration({variables: {identifier}});
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const clientKey = queryParams.get("clientKey");

    if (clientKey) {
      setSelectedFormData({...selectedFormData, clientKey});
    }
  }, []);

  const [setSalesforceAccessTokens] = useMutation(SET_SALESFORCE_ACCESS_TOKEN, {
    onCompleted: (data) => {
      updateIntegrationFields(data.setSalesforceAccessTokens, false, true);
      setSelectedFormData(data.setSalesforceAccessTokens.configuration);
    },
    onError: () => handleError(new Error(SALESFORCE_AUTHORIZATION_ERROR_MESSAGE)),
  });

  const [setVantaAccessTokens] = useMutation(SET_VANTA_ACCESS_TOKEN, {
    onCompleted: (data) => {
      updateIntegrationFields(data);
      setSelectedFormData(data.setVantaAccessTokens.configuration);
    },
  });

  const [setZoomAccessTokens] = useMutation(SET_ZOOM_ACCESS_TOKEN, {
    onCompleted: (data) => {
      updateIntegrationFields(
        data.setZoomAccessTokens.configuration,
        false,
        false,
        "zoom",
      );
      setSelectedFormData(data.setZoomAccessTokens.configuration);
    },
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get("code");
    const error = queryParams.get("error");
    const state = queryParams.get("state");
    const type = queryParams.get("type");

    if (type === "salesforce" && error) {
      navigate("/integrations/salesforce", {replace: true, search: ""});
      try {
        consumeSalesforceCodeVerifier(state);
      } catch {
        // The authorization may have been canceled after its state expired.
      }
      handleError(new Error(SALESFORCE_AUTHORIZATION_ERROR_MESSAGE));
      return;
    }

    if (code) {
      switch (type) {
        case "salesforce": {
          navigate(`/integrations/${type}`, {replace: true, search: ""});
          try {
            const codeVerifier = consumeSalesforceCodeVerifier(state);
            setSalesforceAccessTokens({variables: {code, codeVerifier}});
          } catch {
            handleError(
              new Error(
                "Salesforce authorization expired. Please reconnect to Salesforce.",
              ),
            );
          }
          break;
        }
        case "vanta":
          navigate(`/integrations/${type}`, {replace: true, search: ""});
          setVantaAccessTokens({variables: {code}});
          break;
        case "zoom":
          navigate(`/integrations/${type}`, {replace: true, search: ""});
          setZoomAccessTokens({variables: {code}});
          break;
        default:
          navigate("/");
      }
    }
  }, []);
};
